import getConfig from 'next/config'
import NodeRSA from 'node-rsa'
import { MOBILE_APP_EVENTS } from './constant'
import { GetServerSidePropsContext } from 'next/types'
import {
  ICheckoutCCardPayPayload,
  ICheckoutConsentTappedPayload,
  ICheckoutReviewVideoImpressionEndPayload,
  ICheckoutReviewVideoImpressionStartPayload,
  ICheckoutReviewViewPayload,
  IMessagePayload,
  IShippingChangePayload
} from 'src/types/interfaces/event'

const rsaPublicKey = getConfig().publicRuntimeConfig.rsaPublicKey

declare global {
  interface Window {
    webkit?: {
      messageHandlers: {
        iOSHandler: {
          postMessage: (message: any) => void
        }
      }
    }
    handleiOSMessage?: (message: any) => void
  }
}

export const toDictionary = <T, K>(
  arr: T[],
  keyFn: (item: T) => string,
  itemFn: (item: T) => K
) => {
  return arr.reduce((total, item) => {
    total[keyFn(item)] = itemFn(item)
    return total
  }, {} as { [key: string]: K })
}

export const rsaEncryptData = (data: any) => {
  if (typeof window !== 'undefined') {
    try {
      const publicKey = new NodeRSA(rsaPublicKey, 'pkcs8-public')
      return publicKey.encrypt(data, 'base64')
    } catch (err: any) {
      console.error(err)
      return ''
    }
  }
  return ''
}

export const mappingStateCode = {
  alabama: 'al',
  alaska: 'ak',
  americansamoa: 'as',
  arizona: 'az',
  arkansas: 'ar',
  california: 'ca',
  colorado: 'co',
  connecticut: 'ct',
  delaware: 'de',
  districtofcolumbia: 'dc',
  federatedstatesofmicronesia: 'fm',
  florida: 'fl',
  georgia: 'ga',
  guam: 'gu',
  hawaii: 'hi',
  idaho: 'id',
  illinois: 'il',
  indiana: 'in',
  iowa: 'ia',
  kansas: 'ks',
  kentucky: 'ky',
  louisiana: 'la',
  maine: 'me',
  marshallislands: 'mh',
  maryland: 'md',
  massachusetts: 'ma',
  michigan: 'mi',
  minnesota: 'mn',
  mississippi: 'ms',
  missouri: 'mo',
  montana: 'mt',
  nebraska: 'ne',
  nevada: 'nv',
  newhampshire: 'nh',
  newjersey: 'nj',
  newmexico: 'nm',
  newyork: 'ny',
  northcarolina: 'nc',
  northdakota: 'nd',
  northernmarianaislands: 'mp',
  ohio: 'oh',
  oklahoma: 'ok',
  oregon: 'or',
  palau: 'pw',
  pennsylvania: 'pa',
  puertorico: 'pr',
  rhodeisland: 'ri',
  southcarolina: 'sc',
  southdakota: 'sd',
  tennessee: 'tn',
  texas: 'tx',
  utah: 'ut',
  vermont: 'vt',
  virginislands: 'vi',
  virginia: 'va',
  washington: 'wa',
  westvirginia: 'wv',
  wisconsin: 'wi',
  wyoming: 'wy'
}

type AppMessageType = keyof typeof MOBILE_APP_EVENTS

type AppMessagePayload = {
  [MOBILE_APP_EVENTS.CHECKOUT_REVIEW_VIEW]: ICheckoutReviewViewPayload
  [MOBILE_APP_EVENTS.CHECKOUT_REVIEW_VIDEO_IMPRESSION_START]: ICheckoutReviewVideoImpressionStartPayload
  [MOBILE_APP_EVENTS.CHECKOUT_REVIEW_VIDEO_IMPRESSION_END]: ICheckoutReviewVideoImpressionEndPayload
  [MOBILE_APP_EVENTS.SHIPPING_CHANGE]: IShippingChangePayload
  [MOBILE_APP_EVENTS.CHECKOUT_CONSENT_TAPPED]: ICheckoutConsentTappedPayload
  [MOBILE_APP_EVENTS.CHECKOUT_CARD_PAY]: ICheckoutCCardPayPayload
  [key: string]: IMessagePayload
}

interface IAppMessage<T extends AppMessageType> {
  type: T
  payload?: AppMessagePayload[T]
  [x: string]: any
}

/**
 * Mapping of app client versions to their corresponding build numbers. This is used to determine which features or messages to send based on the app version.
 * Please refer to https://crossian.atlassian.net/wiki/spaces/tech/pages/1944682514/ShopHelp+iOS+App+-+Release+Version+List#4.-Version-Control for the latest version information.
 */
export const appClientVersion = {
  ['1.0.0']: 37,
  ['1.0.1']: 43,
  ['1.1.0']: 49,
  ['1.2.0']: 53,
  ['1.3.0']: 57,
  ['1.4.0']: 61,
  ['1.5.0']: 73,
  ['1.6.0']: 78,
  ['1.7.0']: 83,
  ['1.8.0']: 86,
  ['1.9.0']: 91
} as const

export function sendMessageToApp<T extends AppMessageType>(message: IAppMessage<T>) {
  if (
    window.webkit &&
    window.webkit?.messageHandlers &&
    window.webkit?.messageHandlers?.iOSHandler
  ) {
    window.webkit.messageHandlers.iOSHandler.postMessage(JSON.stringify(message))
  } else {
    console.log('Not in iOS WebView or handler not available')
  }
}

export function decodeJSONParameter<T = any>(value: string, fallback: T = {} as T) {
  if (!value) return fallback
  try {
    return JSON.parse(decodeURIComponent(value))
  } catch {
    try {
      return JSON.parse(value)
    } catch {
      return fallback
    }
  }
}

export function decodeStringParameter(value?: string, fallback = '') {
  if (!value) return fallback
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function appendXHeadersToCookies(context: GetServerSidePropsContext) {
  const { req, res } = context

  const existingCookies = res.getHeader('Set-Cookie')
  const cookies: string[] = existingCookies
    ? Array.isArray(existingCookies)
      ? existingCookies.map(String)
      : [String(existingCookies)]
    : []

  Object.entries(req.headers).forEach(([key, value]) => {
    if ((key.startsWith('x-') || key.startsWith('X-')) && value) {
      cookies.push(`${key}=${value}; Path=/; Secure; SameSite=Lax; Max-Age=31536000`)
    }
  })

  res.setHeader('Set-Cookie', cookies)
}
